import { createFileRoute, Link, stripSearchParams, useNavigate } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { PageShell } from "@/components/page-shell";
import { InteractiveMap } from "@/components/map/interactive-map";
import { getState } from "@/lib/states";
import { riskLabel } from "@/lib/risk";
import {
  DEFAULT_LAYER,
  layerDescription,
  layerLabel,
  validateMapSearch,
  type MapSearch,
} from "@/lib/map/search-params";
import { breadcrumbJsonLd } from "@/lib/jsonld";
import { absUrl, ogImageUrl } from "@/lib/site";
import { metaDescription, pageTitle } from "@/lib/seo";

/**
 * Everything about a view of the map lives in the URL, so the loader turns the
 * validated search into the copy the <head> needs. Canonical deliberately drops
 * `layer` and `z`: those are ways of looking at a place, not separate pages, and
 * indexing every permutation would bury the pages that matter.
 */
function describeView(search: MapSearch) {
  const state = search.state ? getState(search.state) : undefined;
  const layer = layerLabel[search.layer];
  // The share image and title name the layer only when it isn't the default, so the
  // common card stays clean but a link to the season view still says "season".
  const layerNote = search.layer === DEFAULT_LAYER ? "" : `: ${layer}`;

  if (state && search.county) {
    const countyName = search.county.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const county = `${countyName} ${state.abbr === "LA" ? "Parish" : "County"}`;
    return {
      canonical: `/map?state=${state.slug}&county=${search.county}`,
      title: pageTitle(`${county}, ${state.name}: Lone Star Tick Map`),
      heading: `Lone Star ticks: ${county}, ${state.name}`,
      description: metaDescription(
        `Where Lone Star ticks are reported around ${county}, ${state.name}, on an interactive map showing ${layer.toLowerCase()}, with prevention and bite steps.`,
      ),
      ogTitle: `${county}, ${state.name}${layerNote}`,
      ogSubtitle: `Peak season: ${state.peakSeason}`,
      badge: riskLabel[state.risk],
      level: state.risk as string | undefined,
      stateSlug: state.slug as string | undefined,
    };
  }
  if (state) {
    return {
      canonical: `/map?state=${state.slug}`,
      title: pageTitle(`${state.name} Lone Star Tick Map by County`),
      heading: `Lone Star ticks in ${state.name}`,
      description: metaDescription(
        `Interactive map of Lone Star ticks in ${state.name}: ${layer.toLowerCase()}, peak season and county-level detail.`,
      ),
      ogTitle: `Lone Star ticks in ${state.name}${layerNote}`,
      ogSubtitle: `Peak season: ${state.peakSeason}`,
      badge: riskLabel[state.risk],
      level: state.risk as string | undefined,
      stateSlug: state.slug as string | undefined,
    };
  }
  return {
    canonical: "/map",
    title: pageTitle("Lone Star Tick Map of the U.S."),
    heading: "Lone Star Tick Map",
    description:
      "Interactive map of where Lone Star ticks are found in the US, with each state's peak season and how to prevent bites. Zoom in to see the counties.",
    ogTitle: `Lone Star tick map of the U.S.${layerNote}`,
    ogSubtitle: layerDescription[search.layer],
    badge: undefined,
    level: undefined,
  };
}

export const Route = createFileRoute("/map")({
  // SearchSchemaInput tells the router that every field is optional *going in*,
  // so a plain <Link to="/map"> stays valid while the parsed output keeps `layer`
  // guaranteed for everything downstream.
  validateSearch: (input: Partial<MapSearch> & SearchSchemaInput): MapSearch =>
    validateMapSearch(input),
  // The validator always resolves `layer`, so without this the router would notice
  // the parsed search differs from the URL and 307 every visitor to
  // /map?layer=awareness. Stripping the default keeps the plain URL plain.
  search: { middlewares: [stripSearchParams({ layer: DEFAULT_LAYER })] },
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ deps }) => describeView(deps.search),
  head: ({ loaderData }) => {
    const view = loaderData ?? describeView({ layer: "awareness" });
    const og = ogImageUrl({
      title: view.ogTitle,
      subtitle: view.ogSubtitle,
      badge: view.badge,
      level: view.level,
      kind: "map",
      state: view.stateSlug,
    });
    return {
      meta: [
        { title: view.title },
        { name: "description", content: view.description },
        // A state or county view of the map would compete in search with that place's
        // own page, which answers the same question in full. It stays shareable and its
        // links are still followed; only /map itself is indexed.
        ...(view.canonical === "/map" ? [] : [{ name: "robots", content: "noindex, follow" }]),
        { property: "og:title", content: view.ogTitle },
        { property: "og:description", content: view.description },
        { property: "og:url", content: absUrl(view.canonical) },
        { property: "og:image", content: og },
        { name: "twitter:title", content: view.ogTitle },
        { name: "twitter:image", content: og },
      ],
      links: [{ rel: "canonical", href: absUrl(view.canonical) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Map", path: "/map" },
            ]),
          ),
        },
      ],
    };
  },
  component: MapPage,
});

function MapPage() {
  const search = Route.useSearch();
  const view = Route.useLoaderData();
  const navigate = useNavigate({ from: Route.fullPath });

  const update = (next: Partial<MapSearch>, replace: boolean) =>
    navigate({
      search: (prev) => ({ ...prev, ...next }),
      replace,
      resetScroll: false,
    });

  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Map", path: "/map" },
      ]}
      title={view.heading}
      intro="Where Lone Star ticks are reported in the U.S. Tap your state or search your ZIP code to see what it means for you."
    >
      <InteractiveMap
        search={search}
        onNavigate={(next) => update(next, false)}
        onReplace={(next) => update(next, true)}
      />

      <p className="text-xs leading-relaxed text-muted-foreground">
        Finding ticks in an area doesn&rsquo;t mean a bite will make you ill. The map is a prompt to
        use prevention where ticks live. It summarises published tick data; it is not official
        surveillance or medical advice. See our{" "}
        <Link to="/sources" className="font-semibold text-primary underline">
          sources
        </Link>
        .
      </p>
    </PageShell>
  );
}
